import { useOutletContext } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { LOCAL_DATETIME_FMT, formatDate } from 'lib/helpers/dateUtils';
import { useStepperContext } from 'lib/context/StepperContext/StepperContext';
import { useGetUserTrainings } from 'lib/hooks/queries/trainings/useGetTrainings';

import { currentPractice } from 'components/practice/practiceState';
import Accordion from 'components/shared/Stepper/components/Accordion/Accordion';
import AcknowledgementList from 'components/shared/AcknowledgementList/AcknowledgementList';

import { WIZARD_PROGRESS } from '../../lib/enum';
import InfoBanner from '../InfoBanner/InfoBanner';
import { findMeetingForStep } from '../LiveTrainingStep/lib/config';

import Header from './components/Header';
import { responsibilitiesData } from './lib/helpers';
import ResponsibilityList from './components/ResponsibilityList';

const COMPLETED_STATUSES = [WIZARD_PROGRESS.COMPLETED, WIZARD_PROGRESS.CONFIRMED];

const Responsibilities = ({
  isAdmin,
  addFooterActions,
  setFooterConfig,
  setHeaderButtonProps,
  setHeaderInfoTooltip,
  onNext,
  mutateOrchestratorData,
  orchestratorData
}) => {
  const practice = useRecoilValue(currentPractice);
  const timezone = practice?.timezone;
  const { wizardState } = useStepperContext();
  const isCompleted = COMPLETED_STATUSES.includes(orchestratorData?.handlerStatus);
  const [term, setTerm] = useState(false);

  const { practiceId } = useOutletContext();
  const { data } = useGetUserTrainings({
    params: { practiceId: Number(practiceId) },
    dependencies: [practiceId]
  });

  const defaultMeetings = data?.meetings?.filter((meeting) => meeting.is_default) || [];

  const responsibilitiesWithDates = useMemo(() => {
    return responsibilitiesData.map((responsibility) => {
      const meeting = findMeetingForStep(
        defaultMeetings,
        responsibility.stepCode,
        wizardState?.steps
      );

      let dateText = 'Not set';
      if (meeting?.starts_at) {
        dateText = formatDate(meeting.starts_at, timezone, LOCAL_DATETIME_FMT);
      }

      return {
        ...responsibility,
        dateText
      };
    });
  }, [defaultMeetings, timezone, wizardState?.steps]);

  const checkboxText = (
    <div>
      I acknowledge and agree to <span className="font-700">complete</span> these
      <span className="font-700"> responsibilities</span> by the listed due dates.
    </div>
  );

  useEffect(() => {
    setHeaderButtonProps(null);
    addFooterActions([
      {
        text: isAdmin ? 'Continue' : isCompleted ? 'Continue' : 'Accept & continue',
        onClick: () => {
          if (isAdmin) return onNext();
          if (isCompleted) return onNext();
          mutateOrchestratorData({ acknowledge: true });
        },
        isDisabled: !isAdmin && !term && !isCompleted
      }
    ]);
    setFooterConfig((prev) => ({
      ...prev,
      aboveFooterContent: (
        <AcknowledgementList
          renderItem={() => null}
          acknowledgement={{
            text: checkboxText,
            isChecked: isCompleted || term,
            onChange: () => setTerm((prev) => !prev),
            isDisabled: isCompleted || isAdmin
          }}
          showBanner={false}
          showHr={false}
        />
      )
    }));

    return () => {
      setFooterConfig((prev) => ({ ...prev, aboveFooterContent: null }));
    };
  }, [
    term,
    isCompleted,
    isAdmin,
    addFooterActions,
    setHeaderButtonProps,
    onNext,
    mutateOrchestratorData
  ]);

  return (
    <div className="flex flex-col gap-4">
      <InfoBanner
        title="Priority Notice"
        description="To maximize your time with our trainers, complete every item before its corresponding Live Working Session. Arriving prepared ensures hands-on practice and fewer follow-ups."
        onDismiss={setHeaderInfoTooltip}
      />
      {responsibilitiesWithDates.map((responsibility) => (
        <Accordion
          key={responsibility.id}
          stepTitle={responsibility.title}
          panelClassName="bg-neutral-dark-25"
          practiceId={practiceId}
          ButtonComponent={
            <Header
              title={responsibility.title}
              icon={responsibility.icon}
              date={responsibility.dateText}
            />
          }
          panelComponent={<ResponsibilityList list={responsibility.descriptionArray} />}
        />
      ))}
    </div>
  );
};

export default Responsibilities;
